'use client';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  applyLocationInventoryAdjustment,
  fetchLocationAdjustmentTargets,
  queryStorageSiteBalances,
  resolveTomVendingInventoryUpc,
  syncTomVendingAdjustment,
  type LocationAdjustmentTargetMachine,
  type LocationAdjustmentWarehouseTarget,
  type MachineBalanceApiRow,
} from '@/hooks/useTomVendingInventory';
import { useInventoryRoomSync, rowIsCleanForRemoteMerge } from '@/hooks/useInventoryRoomSync';
import type { InventoryCountUpdatedEvent } from '@/realtime/inventorySocket';
import { useAuth } from '@/contexts/AuthContext';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';
import { useNotification } from '@/contexts/NotificationContext';
import { formatTomVendingLastAudit } from '@/utils/tomVendingLastAudit';

const rowKey = (storageSiteId: number, inventoryItemId: number) => `${storageSiteId}-${inventoryItemId}`;
const TV_ADJUST_AUTO_SAVE_MS = 750;

const TomsVendingLocationInventoryAdjustmentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialLoc = parseInt(String(searchParams.get('locationId') || ''), 10);
  const { data: locations = [], isLoading: locationsLoading } = useTomVendingLocationsList();
  const { showError } = useNotification();
  const { user } = useAuth();
  const currentUserId =
    user?.id != null ? parseInt(String(user.id), 10) : user?.user?.id != null ? parseInt(String(user.user.id), 10) : null;
  const lastSyncAtRef = useRef(new Date(0).toISOString());

  const [selectedLocation, setSelectedLocation] = useState<{ id: number; name?: string } | null>(null);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targets, setTargets] = useState<{
    warehouse: LocationAdjustmentWarehouseTarget | null;
    machines: LocationAdjustmentTargetMachine[];
  } | null>(null);
  const [activeStorageSiteId, setActiveStorageSiteId] = useState<number | null>(null);

  const [rows, setRows] = useState<MachineBalanceApiRow[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  const [lastSavedQty, setLastSavedQty] = useState<Record<string, number>>({});
  const [draftQty, setDraftQty] = useState<Record<string, number>>({});
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const savingRowKeysRef = useRef<Set<string>>(new Set());
  const [savingCount, setSavingCount] = useState(0);

  const [manualUpc, setManualUpc] = useState('');

  const lastSavedQtyRef = useRef(lastSavedQty);
  lastSavedQtyRef.current = lastSavedQty;
  const draftQtyRef = useRef(draftQty);
  draftQtyRef.current = draftQty;

  const initialFromUrlApplied = useRef(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (initialFromUrlApplied.current || locationsLoading || locations.length === 0) return;
    initialFromUrlApplied.current = true;
    if (!Number.isNaN(initialLoc) && initialLoc > 0) {
      const m = locations.find((l: { id: number }) => l.id === initialLoc);
      if (m) {
        setSelectedLocation({ id: m.id, name: m.name });
      }
    }
  }, [locationsLoading, locations, initialLoc]);

  useEffect(() => {
    if (!selectedLocation) {
      setTargets(null);
      setActiveStorageSiteId(null);
      setRows([]);
      setLastSavedQty({});
      setDraftQty({});
      return;
    }
    let cancelled = false;
    setTargetsLoading(true);
    void (async () => {
      try {
        const t = await fetchLocationAdjustmentTargets(selectedLocation.id);
        if (cancelled) return;
        setTargets(t);
        const first = t.warehouse?.storageSiteId ?? t.machines[0]?.storageSiteId ?? null;
        setActiveStorageSiteId(first);
        setLastSavedQty({});
        setDraftQty({});
        setRows([]);
        setDataRefreshKey((k) => k + 1);
      } catch (e: unknown) {
        if (!cancelled) showError(e instanceof Error ? e.message : 'Failed to load location');
      } finally {
        if (!cancelled) setTargetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation?.id, showError]);

  useEffect(() => {
    if (!selectedLocation || activeStorageSiteId == null) return;
    let cancelled = false;
    setTableLoading(true);
    void (async () => {
      try {
        const r = await queryStorageSiteBalances({
          locationId: selectedLocation.id,
          storageSiteId: activeStorageSiteId,
          skip: 0,
          take: 5000,
          search: debouncedSearch,
          orderBy: 'upc',
          orderDirection: 'ASC',
          hideZeroQuantity: false,
        });
        if (cancelled) return;
        setRows(r.data);
        setLastSavedQty((prev) => {
          const next = { ...prev };
          for (const row of r.data) {
            const k = rowKey(activeStorageSiteId, row.inventoryItem.id);
            if (savingRowKeysRef.current.has(k)) continue;
            next[k] = row.quantity;
          }
          return next;
        });
        setDraftQty((prev) => {
          const next = { ...prev };
          for (const row of r.data) {
            const k = rowKey(activeStorageSiteId, row.inventoryItem.id);
            if (next[k] === undefined) next[k] = row.quantity;
          }
          return next;
        });
      } catch (e: unknown) {
        if (!cancelled) showError(e instanceof Error ? e.message : 'Failed to load balances');
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation, activeStorageSiteId, debouncedSearch, dataRefreshKey, showError]);

  const saveAdjustment = useCallback(
    async (storageSiteId: number, inventoryItemId: number, quantity?: number) => {
      if (!selectedLocation) return;
      const k = rowKey(storageSiteId, inventoryItemId);
      const qty = quantity ?? draftQty[k] ?? lastSavedQty[k] ?? 0;
      const row = rows.find(
        (r) => r.inventoryItem.id === inventoryItemId && activeStorageSiteId === storageSiteId,
      );
      if (row?.balanceId === 0 && qty === 0) return;
      if (qty === lastSavedQty[k]) return;
      if (savingRowKeysRef.current.has(k)) return;

      savingRowKeysRef.current.add(k);
      setSavingCount((c) => c + 1);
      try {
        await applyLocationInventoryAdjustment({
          locationId: selectedLocation.id,
          lines: [{ storageSiteId, inventoryItemId, quantity: qty }],
        });
        setLastSavedQty((prev) => ({ ...prev, [k]: qty }));
        setDraftQty((prev) => ({ ...prev, [k]: qty }));
        setRows((prev) =>
          prev.map((r) =>
            r.inventoryItem.id === inventoryItemId ? { ...r, quantity: qty, balanceId: r.balanceId || 1 } : r,
          ),
        );
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        savingRowKeysRef.current.delete(k);
        setSavingCount((c) => Math.max(0, c - 1));
      }
    },
    [selectedLocation, draftQty, lastSavedQty, rows, activeStorageSiteId, showError],
  );

  const scheduleSaveAdjustment = useCallback(
    (storageSiteId: number, inventoryItemId: number) => {
      const k = rowKey(storageSiteId, inventoryItemId);
      const existing = saveTimersRef.current[k];
      if (existing) window.clearTimeout(existing);
      saveTimersRef.current[k] = window.setTimeout(() => {
        delete saveTimersRef.current[k];
        void saveAdjustment(storageSiteId, inventoryItemId);
      }, TV_ADJUST_AUTO_SAVE_MS);
    },
    [saveAdjustment],
  );

  const changeDraft = useCallback(
    (storageSiteId: number, inventoryItemId: number, raw: string) => {
      const n = parseInt(raw.replace(/[^\d]/g, '') || '0', 10);
      const q = Number.isNaN(n) || n < 0 ? 0 : n;
      const k = rowKey(storageSiteId, inventoryItemId);
      setDraftQty((prev) => ({ ...prev, [k]: q }));
      scheduleSaveAdjustment(storageSiteId, inventoryItemId);
    },
    [scheduleSaveAdjustment],
  );

  const applyRemoteAdjustment = useCallback(
    (storageSiteId: number, inventoryItemId: number, quantity: number, updatedAt: string) => {
      if (activeStorageSiteId != null && storageSiteId !== activeStorageSiteId) return;
      const k = rowKey(storageSiteId, inventoryItemId);
      if (savingRowKeysRef.current.has(k)) return;
      if (saveTimersRef.current[k]) return;
      const saved = lastSavedQtyRef.current[k];
      const draft = draftQtyRef.current[k] ?? saved ?? quantity;
      if (!rowIsCleanForRemoteMerge(draft, saved)) return;

      setLastSavedQty((prev) => ({ ...prev, [k]: quantity }));
      setDraftQty((prev) => ({ ...prev, [k]: quantity }));
      setRows((prev) =>
        prev.map((r) =>
          r.inventoryItem.id === inventoryItemId ? { ...r, quantity, balanceId: r.balanceId || 1 } : r,
        ),
      );
      if (updatedAt > lastSyncAtRef.current) {
        lastSyncAtRef.current = updatedAt;
      }
    },
    [activeStorageSiteId],
  );

  const handleRemoteAdjustmentEvent = useCallback(
    (event: InventoryCountUpdatedEvent) => {
      if (event.domain !== 'tv_adjustment') return;
      if (event.quantity == null || event.storageSiteId == null) return;
      applyRemoteAdjustment(event.storageSiteId, event.inventoryItemId, event.quantity, event.updatedAt);
    },
    [applyRemoteAdjustment],
  );

  const pollAdjustmentSync = useCallback(async () => {
    if (!selectedLocation || activeStorageSiteId == null) return;
    const since = lastSyncAtRef.current;
    const r = await syncTomVendingAdjustment({
      locationId: selectedLocation.id,
      since,
      storageSiteId: activeStorageSiteId,
    });
    for (const line of r.lines) {
      applyRemoteAdjustment(line.storageSiteId, line.inventoryItemId, line.quantity, line.updatedAt);
    }
    const maxAt = r.lines.reduce((m, l) => (l.updatedAt > m ? l.updatedAt : m), since);
    if (maxAt > since) {
      lastSyncAtRef.current = maxAt;
    }
  }, [selectedLocation, activeStorageSiteId, applyRemoteAdjustment]);

  useInventoryRoomSync({
    enabled: selectedLocation != null && activeStorageSiteId != null,
    join:
      selectedLocation && activeStorageSiteId != null
        ? {
            scope: 'tv_adjustment',
            locationId: selectedLocation.id,
            storageSiteId: activeStorageSiteId,
          }
        : null,
    currentUserId,
    onRemoteUpdate: handleRemoteAdjustmentEvent,
    poll: pollAdjustmentSync,
  });

  const handleAddUpc = useCallback(async () => {
    if (!selectedLocation || activeStorageSiteId == null) {
      showError('Pick a location and storage tab first');
      return;
    }
    const code = manualUpc.trim();
    if (!code) return;
    try {
      const res = await resolveTomVendingInventoryUpc(code, { autoCreateInCatalog: true });
      if (!res.found || res.inventoryItemId == null) {
        showError(
          'No catalog match for this UPC. Add it in Catalog admin, or scan again when product data is available.',
        );
        return;
      }
      const iid = res.inventoryItemId;
      const k = rowKey(activeStorageSiteId, iid);
      if (!rows.some((r) => r.inventoryItem.id === iid)) {
        const thumb = res.thumbnailUrl || res.imageUrl;
        const synthetic: MachineBalanceApiRow = {
          balanceId: 0,
          quantity: 0,
          inventoryItem: {
            id: iid,
            upc: res.primaryUpc ?? code,
            name: res.name ?? null,
            description: null,
            imageS3Key: null,
            thumbnailS3Key: null,
            imageUrl: res.imageUrl ?? null,
            thumbnailUrl: thumb ?? null,
            variantUpcs: [],
          },
        };
        setRows((prev) => [...prev, synthetic]);
      }
      setLastSavedQty((prev) => ({ ...prev, [k]: prev[k] ?? 0 }));
      setDraftQty((prev) => ({ ...prev, [k]: prev[k] ?? 0 }));
      setManualUpc('');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Lookup failed');
    }
  }, [selectedLocation, activeStorageSiteId, manualUpc, rows, showError]);

  const tabLabel = (siteId: number) => {
    if (!targets) return String(siteId);
    if (targets.warehouse?.storageSiteId === siteId) {
      return targets.warehouse.label?.trim() || 'Warehouse';
    }
    const m = targets.machines.find((x) => x.storageSiteId === siteId);
    if (m) {
      const t = m.title?.trim();
      return t ? `${m.assetId} — ${t}` : m.assetId;
    }
    return `Site ${siteId}`;
  };

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Locations', href: '/app/toms-vending/locations' },
    { label: 'Location stock adjustment' },
  ];

  const siteTabIds = useMemo(() => {
    if (!targets) return [];
    const ids: number[] = [];
    if (targets.warehouse) ids.push(targets.warehouse.storageSiteId);
    for (const m of targets.machines) ids.push(m.storageSiteId);
    return ids;
  }, [targets]);

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title="Location stock adjustment" />
      <Box sx={{ p: 2, maxWidth: 1100 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set on-hand quantity at this location’s warehouse and each vending machine. Quantities save automatically as
          you edit. Changes are written as <strong>adjustment</strong> movements (delta from the previous balance). Use
          this for initial counts or correcting drift. Site visits and fills still use their own movement reasons.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <Autocomplete
            sx={{ minWidth: 320 }}
            options={locations}
            loading={locationsLoading}
            getOptionLabel={(o: { id: number; name?: string }) => o.name || `Location #${o.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={
              selectedLocation
                ? locations.find((l: { id: number }) => l.id === selectedLocation.id) ?? null
                : null
            }
            onChange={(_, v) => setSelectedLocation(v ? { id: v.id, name: v.name } : null)}
            renderInput={(params) => <TextField {...params} label="Location" size="small" />}
            renderOption={(props, option: { id: number; name?: string }) => {
              const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
              return (
                <li key={key} {...rest}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tomVendingLocationIsWarehouse(option) ? (
                      <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
                    ) : null}
                    <span>{option.name || `Location #${option.id}`}</span>
                  </Box>
                </li>
              );
            }}
          />
          {selectedLocation ? (
            <Button
              component={RouterLink}
              to={`/app/toms-vending/locations/${selectedLocation.id}`}
              size="small"
              sx={{ mt: 0.5, textTransform: 'none' }}
            >
              Location details…
            </Button>
          ) : null}
          <Button
            component={RouterLink}
            to="/app/toms-vending/inventory/movements?reason=adjustment"
            size="small"
            sx={{ mt: 0.5, textTransform: 'none' }}
          >
            View adjustment movements…
          </Button>
        </Box>

        {targetsLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Loading storage sites…
          </Typography>
        )}

        {selectedLocation && targets && siteTabIds.length > 0 && (
          <>
            {activeStorageSiteId === targets.warehouse?.storageSiteId && targets.warehouse ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Last warehouse audit:{' '}
                {formatTomVendingLastAudit(
                  targets.warehouse.lastAuditAt,
                  targets.warehouse.lastAuditByDisplayName,
                )}
              </Typography>
            ) : null}
            <Tabs
              value={activeStorageSiteId ?? false}
              onChange={(_, v: number) => {
                setActiveStorageSiteId(v);
                setSearch('');
                setDebouncedSearch('');
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, minHeight: 42, '& .MuiTab-root': { textTransform: 'none' } }}
            >
              {siteTabIds.map((sid) => (
                <Tab key={sid} label={tabLabel(sid)} value={sid} />
              ))}
            </Tabs>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
              <TextField
                size="small"
                label="Search UPC / name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 260 }}
                slotProps={{
                  input: {
                    endAdornment:
                      search.length > 0 ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            edge="end"
                            onClick={() => setSearch('')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                  },
                }}
              />
              <TextField
                size="small"
                label="Add SKU by UPC"
                value={manualUpc}
                onChange={(e) => setManualUpc(e.target.value)}
                sx={{ width: 200 }}
                slotProps={{
                  input: {
                    endAdornment:
                      manualUpc.length > 0 ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear UPC"
                            edge="end"
                            onClick={() => setManualUpc('')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                  },
                }}
              />
              <Button variant="outlined" size="small" onClick={() => void handleAddUpc()} sx={{ textTransform: 'none' }}>
                Look up
              </Button>
              {savingCount > 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  Saving…
                </Typography>
              ) : null}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>UPC</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          Loading…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No rows yet. Add a catalog UPC or confirm this storage site has balances.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeStorageSiteId != null &&
                    rows.map((r) => {
                      const k = rowKey(activeStorageSiteId, r.inventoryItem.id);
                      const src = r.inventoryItem.thumbnailUrl || r.inventoryItem.imageUrl;
                      const draft = draftQty[k] ?? lastSavedQty[k] ?? r.quantity;
                      return (
                        <TableRow key={k}>
                          <TableCell>
                            {src ? (
                              <Avatar src={src} variant="rounded" sx={{ width: 36, height: 36 }} />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 36, height: 36 }}>
                                <Inventory2Icon fontSize="small" />
                              </Avatar>
                            )}
                          </TableCell>
                          <TableCell>{r.inventoryItem.upc}</TableCell>
                          <TableCell>{r.inventoryItem.name?.trim() || '—'}</TableCell>
                          <TableCell align="right" sx={{ maxWidth: 120 }}>
                            <TextField
                              size="small"
                              type="text"
                              inputMode="numeric"
                              value={draft === 0 ? '' : String(draft)}
                              placeholder="0"
                              onChange={(e) => changeDraft(activeStorageSiteId, r.inventoryItem.id, e.target.value)}
                              sx={{ width: 96 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {selectedLocation && targets && siteTabIds.length === 0 && !targetsLoading ? (
          <Typography variant="body2" color="text.secondary">
            No warehouse or vending machines with storage sites for this location yet.
          </Typography>
        ) : null}
      </Box>
    </>
  );
};

export default TomsVendingLocationInventoryAdjustmentPage;
